import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  IconButton,
  Chip,
  Stack,
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  TextField,
  MenuItem,
  Card,
  CardContent,
  Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import UndoIcon from '@mui/icons-material/Undo';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RefreshIcon from '@mui/icons-material/Refresh';
import PaymentIcon from '@mui/icons-material/Payment';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import { toast } from 'react-toastify';
import { useLanguage } from '../../contexts/LanguageContext';
import { useConfirm } from '../../hooks/useConfirm';
import checkPaymentsService from '../../services/checkPayments.service';
import CheckPaymentDialog from '../../components/vouchers/CheckPaymentDialog';
import VoucherPreviewDialog from '../../components/vouchers/VoucherPreviewDialog';
import { VoucherDto } from '../../types/vouchers.types';
import config from '../../config';
import axios from 'axios';

const CheckPaymentsPage: React.FC = () => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const confirm = useConfirm();

  const userRoles = (() => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return [] as string[];
      const payload = JSON.parse(atob(token.split('.')[1]));
      const roles = payload['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
      return roles ? (Array.isArray(roles) ? roles : [roles]) : [];
    } catch { return [] as string[]; }
  })();
  const canPost = (() => { try { const token = localStorage.getItem('token'); if (!token) return false; const payload = JSON.parse(atob(token.split('.')[1])); const perms = payload.Permission ? (Array.isArray(payload.Permission) ? payload.Permission : [payload.Permission]) : []; return perms.some((p: string) => p.endsWith('_POST')); } catch { return false; } })();

  const [vouchers, setVouchers] = useState<VoucherDto[]>([]);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [filteredVouchers, setFilteredVouchers] = useState<VoucherDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [editVoucher, setEditVoucher] = useState<VoucherDto | null>(null);
  const [previewVoucher, setPreviewVoucher] = useState<VoucherDto | null>(null);
  const [checksParentAccountId, setChecksParentAccountId] = useState<number | null>(null);
  
  const [statusFilter, setStatusFilter] = useState<string>('All');
  const [searchTerm, setSearchTerm] = useState<string>('');

  const [stats, setStats] = useState({
    total: 0,
    draft: 0,
    posted: 0,
    totalAmount: 0
  });

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    loadChecksParentAccount();
    loadVouchers();
  }, []);

  useEffect(() => {
    applyFilters();
    calculateStats();
  }, [vouchers, statusFilter, searchTerm]);

  const loadChecksParentAccount = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${config.API_BASE_URL}/SystemSettings/BankParentAccountId`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data && response.data.value) {
        const accountId = parseInt(response.data.value);
        setChecksParentAccountId(accountId);
      } else {
        showSnackbar(isRTL 
          ? 'لم يتم تكوين حساب البنوك. يرجى الذهاب إلى الإعدادات → المحاسبة'
          : 'Bank parent account not configured. Please go to Settings → Accounting', 'error');
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to load bank parent account');
      showSnackbar(isRTL
        ? 'لم يتم تكوين حساب البنوك. يرجى الذهاب إلى الإعدادات → المحاسبة'
        : 'Bank parent account not configured. Please go to Settings → Accounting', 'error');
    }
  };

  const applyFilters = () => {
    if (!Array.isArray(vouchers)) {
      setFilteredVouchers([]);
      return;
    }

    let filtered = vouchers.filter(v => v.voucherType === 'CHP');

    if (statusFilter !== 'All') {
      filtered = filtered.filter(v => v.status === statusFilter);
    }

    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(v =>
        v.voucherNumber.toLowerCase().includes(search) ||
        (v.payeeName && v.payeeName.toLowerCase().includes(search)) ||
        (v.chequeNumber && v.chequeNumber.toLowerCase().includes(search)) ||
        (v.notes && v.notes.toLowerCase().includes(search))
      );
    }

    setFilteredVouchers(filtered);
  };

  const calculateStats = () => {
    if (!Array.isArray(vouchers)) return;
    const chpVouchers = vouchers.filter(v => v.voucherType === 'CHP');
    const total = chpVouchers.length;
    const draft = chpVouchers.filter(v => v.status === 'Draft').length;
    const posted = chpVouchers.filter(v => v.status === 'Posted').length;
    const totalAmount = chpVouchers.reduce((sum, v) => sum + v.amount, 0);
    setStats({ total, draft, posted, totalAmount });
  };

  const loadVouchers = async () => {
    try {
      setLoading(true);
      const allVouchers = await checkPaymentsService.getAll();
      if (Array.isArray(allVouchers)) {
        const chpOnly = allVouchers.filter(v => v.voucherType === 'CHP').sort((a, b) => b.voucherId - a.voucherId);
        setVouchers(chpOnly);
      } else {
        setVouchers([]);
        showSnackbar(isRTL ? 'خطأ في تحميل البيانات' : 'Error loading data', 'error');
      }
    } catch (err: any) {
      setVouchers([]);
      showSnackbar(isRTL ? 'فشل تحميل السندات' : 'Failed to load vouchers', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const closeSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleAdd = () => {
    if (!checksParentAccountId) {
      showSnackbar(isRTL
        ? 'لم يتم تكوين حساب البنوك. يرجى الذهاب إلى الإعدادات → المحاسبة'
        : 'Bank parent account not configured. Please go to Settings → Accounting', 'error');
      return;
    }
    setEditVoucher(null);
    setDialogOpen(true);
  };

  const handleEdit = (voucher: VoucherDto) => {
    if (voucher.status === 'Posted') {
      showSnackbar(isRTL ? 'لا يمكن تعديل سند مرحّل' : 'Cannot edit posted voucher', 'error');
      return;
    }
    setEditVoucher(voucher);
    setDialogOpen(true);
  };

  const handlePreview = (voucher: VoucherDto) => {
    setPreviewVoucher(voucher);
    setPreviewDialogOpen(true);
  };

  const handleDelete = async (voucher: VoucherDto) => {
    if (voucher.status === 'Posted') {
      showSnackbar(isRTL ? 'لا يمكن حذف سند مرحّل' : 'Cannot delete posted voucher', 'error');
      return;
    }
    const ok = await confirm({ message: isRTL ? 'هل أنت متأكد من الحذف؟' : 'Are you sure you want to delete?', severity: 'error' });
    if (!ok) return;
    setActionLoading(`delete-${voucher.voucherId}`);
    try {
      await checkPaymentsService.delete(voucher.voucherId);
      showSnackbar(isRTL ? 'تم الحذف بنجاح' : 'Deleted successfully', 'success');
      await loadVouchers();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to delete');
      showSnackbar(err.message || (isRTL ? 'فشل الحذف' : 'Failed to delete'), 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handlePost = async (voucher: VoucherDto) => {
    if (voucher.status === 'Posted') {
      showSnackbar(isRTL ? 'السند مرحّل بالفعل' : 'Voucher already posted', 'info');
      return;
    }
    const ok = await confirm({ message: isRTL ? 'هل تريد ترحيل السند؟' : 'Do you want to post this voucher?', severity: 'warning' });
    if (!ok) return;
    setActionLoading(`post-${voucher.voucherId}`);
    try {
      await checkPaymentsService.post(voucher.voucherId);
      showSnackbar(isRTL ? 'تم الترحيل بنجاح' : 'Posted successfully', 'success');
      await loadVouchers();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to post');
      showSnackbar(err.message || (isRTL ? 'فشل الترحيل' : 'Failed to post'), 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleUnpost = async (voucher: VoucherDto) => {
    if (voucher.status === 'Draft') {
      showSnackbar(isRTL ? 'السند غير مرحّل' : 'Voucher not posted', 'info');
      return;
    }
    const ok = await confirm({ message: isRTL ? 'هل تريد إلغاء الترحيل؟' : 'Do you want to unpost this voucher?', severity: 'warning' });
    if (!ok) return;
    setActionLoading(`unpost-${voucher.voucherId}`);
    try {
      await checkPaymentsService.unpost(voucher.voucherId);
      showSnackbar(isRTL ? 'تم إلغاء الترحيل بنجاح' : 'Unposted successfully', 'success');
      await loadVouchers();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to unpost');
      showSnackbar(err.message || (isRTL ? 'فشل إلغاء الترحيل' : 'Failed to unpost'), 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSaveVoucher = async (dto: any) => {
    try {
      if (dto.voucherId) {
        await checkPaymentsService.update(dto.voucherId, dto);
        showSnackbar(isRTL ? 'تم التحديث بنجاح' : 'Updated successfully', 'success');
      } else {
        await checkPaymentsService.create(dto);
        showSnackbar(isRTL ? 'تم الإنشاء بنجاح' : 'Created successfully', 'success');
      }
      setDialogOpen(false);
      setEditVoucher(null);
      await loadVouchers();
    } catch (err: any) {
      showSnackbar(err.message || (isRTL ? 'فشل الحفظ' : 'Failed to save'), 'error');
      throw err;
    }
  };

  return (
    <Box sx={{ p: 3 }} dir={isRTL ? 'rtl' : 'ltr'}>
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Box sx={{ flex: '1 1 calc(25% - 16px)', minWidth: '200px' }}>
          <Card elevation={4} sx={{ background: 'linear-gradient(135deg, #f44336 0%, #e91e63 100%)', color: 'white', transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-5px)' } }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box><Typography variant="h3" fontWeight="bold">{stats.total}</Typography><Typography variant="body2">{isRTL ? 'إجمالي السندات' : 'Total'}</Typography></Box>
                <PaymentIcon sx={{ fontSize: 60, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: '1 1 calc(25% - 16px)', minWidth: '200px' }}>
          <Card elevation={4} sx={{ background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', color: 'white', transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-5px)' } }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box><Typography variant="h3" fontWeight="bold">{stats.draft}</Typography><Typography variant="body2">{isRTL ? 'مسودات' : 'Draft'}</Typography></Box>
                <PendingActionsIcon sx={{ fontSize: 60, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: '1 1 calc(25% - 16px)', minWidth: '200px' }}>
          <Card elevation={4} sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white', transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-5px)' } }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box><Typography variant="h3" fontWeight="bold">{stats.posted}</Typography><Typography variant="body2">{isRTL ? 'مرحّل' : 'Posted'}</Typography></Box>
                <CheckCircleIcon sx={{ fontSize: 60, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: '1 1 calc(25% - 16px)', minWidth: '200px' }}>
          <Card elevation={4} sx={{ background: 'linear-gradient(135deg, #fbc2eb 0%, #a6c1ee 100%)', color: '#333', transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-5px)' } }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box><Typography variant="h4" fontWeight="bold">{stats.totalAmount.toFixed(3)}</Typography><Typography variant="body2">{isRTL ? 'إجمالي (د.ك)' : 'Total (KWD)'}</Typography></Box>
                <AccountBalanceIcon sx={{ fontSize: 60, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      <Paper elevation={3} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <TrendingDownIcon sx={{ fontSize: 32, color: '#f44336' }} />
            <Typography variant="h5" fontWeight="bold" sx={{ color: '#f44336' }}>{isRTL ? 'سندات صرف الشيك' : 'Check Payments'}</Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Tooltip title={isRTL ? 'تحديث' : 'Refresh'}><IconButton color="error" onClick={loadVouchers} sx={{ bgcolor: 'rgba(244, 67, 54, 0.1)', '&:hover': { bgcolor: 'rgba(244, 67, 54, 0.2)' } }}><RefreshIcon /></IconButton></Tooltip>
            <Button variant="contained" color="error" startIcon={<AddIcon />} onClick={handleAdd} disabled={!checksParentAccountId} sx={{ background: 'linear-gradient(45deg, #f44336 30%, #e91e63 90%)', boxShadow: '0 3px 5px 2px rgba(244, 67, 54, .3)', '&:hover': { background: 'linear-gradient(45deg, #d32f2f 30%, #c2185b 90%)' } }}>{isRTL ? 'سند جديد' : 'New'}</Button>
          </Stack>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField label={isRTL ? 'بحث' : 'Search'} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} size="small" sx={{ width: 300 }} />
          <TextField select label={isRTL ? 'الحالة' : 'Status'} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} size="small" sx={{ width: 150 }}>
            <MenuItem value="All">{isRTL ? 'الكل' : 'All'}</MenuItem>
            <MenuItem value="Draft">{isRTL ? 'مسودة' : 'Draft'}</MenuItem>
            <MenuItem value="Posted">{isRTL ? 'مرحّل' : 'Posted'}</MenuItem>
          </TextField>
        </Box>
      </Paper>

      {!checksParentAccountId && <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>{isRTL ? 'لم يتم تكوين حساب البنوك. يرجى الذهاب إلى: الإعدادات → المحاسبة → حساب البنوك الرئيسي' : 'Bank parent account not configured. Go to: Settings → Accounting → Bank Parent Account'}</Alert>}

      <TableContainer component={Paper} elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ background: 'linear-gradient(135deg, #f44336 0%, #e91e63 100%)' }}>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{isRTL ? 'رقم السند' : 'No'}</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{isRTL ? 'التاريخ' : 'Date'}</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{isRTL ? 'المدفوع له' : 'Payee'}</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{isRTL ? 'رقم الشيك' : 'Cheque'}</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{isRTL ? 'البنك' : 'Bank'}</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">{isRTL ? 'المبلغ' : 'Amount'}</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{isRTL ? 'الحالة' : 'Status'}</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">{isRTL ? 'الإجراءات' : 'Actions'}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}><CircularProgress sx={{ color: '#f44336' }} /></TableCell></TableRow> : filteredVouchers.length === 0 ? <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}><Typography color="text.secondary">{isRTL ? 'لا توجد سندات' : 'No vouchers'}</Typography></TableCell></TableRow> : filteredVouchers.map((voucher) => {
              const isDraft = voucher.status === 'Draft';
              return <TableRow key={voucher.voucherId} hover sx={{ '&:hover': { bgcolor: 'rgba(244, 67, 54, 0.05)' } }}>
                <TableCell><Typography fontWeight="bold" color="#f44336">{voucher.voucherNumber}</Typography></TableCell>
                <TableCell>{new Date(voucher.voucherDate).toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB')}</TableCell>
                <TableCell>{voucher.payeeName || '-'}</TableCell>
                <TableCell><Chip label={voucher.chequeNumber || '-'} size="small" sx={{ fontWeight: 'bold', bgcolor: voucher.chequeNumber ? 'rgba(244, 67, 54, 0.1)' : 'transparent', color: voucher.chequeNumber ? '#f44336' : 'text.secondary' }} /></TableCell>
                <TableCell>{voucher.bankName || '-'}</TableCell>
                <TableCell align="right"><Typography fontWeight="bold" color="error.main">{voucher.amount.toFixed(3)}</Typography></TableCell>
                <TableCell><Chip label={isRTL ? (isDraft ? 'مسودة' : 'مرحّل') : (isDraft ? 'Draft' : 'Posted')} color={isDraft ? 'warning' : 'success'} size="small" sx={{ fontWeight: 'bold' }} />{voucher.journalEntryNumber && (<Chip label={`${voucher.journalEntryNumber} (#${voucher.journalEntryId})`} size="small" color="info" variant="outlined" sx={{ ml: 0.5 }} />)}</TableCell>
                <TableCell align="center">
                  <Stack direction="row" spacing={0.5} justifyContent="center">
                    <Tooltip title={isRTL ? 'معاينة' : 'Preview'}><IconButton size="small" onClick={() => handlePreview(voucher)} sx={{ color: '#f44336', '&:hover': { bgcolor: 'rgba(244, 67, 54, 0.1)' } }}><VisibilityIcon fontSize="small" /></IconButton></Tooltip>
                    {isDraft && <><Tooltip title={isRTL ? 'تعديل' : 'Edit'}><IconButton size="small" onClick={() => handleEdit(voucher)} sx={{ color: 'primary.main', '&:hover': { bgcolor: 'rgba(25, 118, 210, 0.1)' } }}><EditIcon fontSize="small" /></IconButton></Tooltip><Tooltip title={isRTL ? 'حذف' : 'Delete'}><IconButton size="small" onClick={() => handleDelete(voucher)} disabled={actionLoading === `delete-${voucher.voucherId}`} sx={{ color: 'error.main', '&:hover': { bgcolor: 'rgba(211, 47, 47, 0.1)' } }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>{canPost && <Tooltip title={isRTL ? 'ترحيل' : 'Post'}><IconButton size="small" onClick={() => handlePost(voucher)} disabled={actionLoading === `post-${voucher.voucherId}`} sx={{ color: 'success.main', '&:hover': { bgcolor: 'rgba(46, 125, 50, 0.1)' } }}><CheckCircleIcon fontSize="small" /></IconButton></Tooltip>}</>}
                    {!isDraft && canPost && <Tooltip title={isRTL ? 'إلغاء الترحيل' : 'Unpost'}><IconButton size="small" onClick={() => handleUnpost(voucher)} disabled={actionLoading === `unpost-${voucher.voucherId}`} sx={{ color: 'warning.main', '&:hover': { bgcolor: 'rgba(237, 108, 2, 0.1)' } }}><UndoIcon fontSize="small" /></IconButton></Tooltip>}
                  </Stack>
                </TableCell>
              </TableRow>;
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {checksParentAccountId && <CheckPaymentDialog open={dialogOpen} onClose={() => { setDialogOpen(false); setEditVoucher(null); }} onSave={handleSaveVoucher} editVoucher={editVoucher} checksParentAccountId={checksParentAccountId} />}
      <VoucherPreviewDialog open={previewDialogOpen} onClose={() => setPreviewDialogOpen(false)} voucher={previewVoucher} />
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={closeSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}><Alert onClose={closeSnackbar} severity={snackbar.severity} sx={{ width: '100%', borderRadius: 2, boxShadow: 3 }}>{snackbar.message}</Alert></Snackbar>
    </Box>
  );
};

export default CheckPaymentsPage;